import React, { useEffect } from "react";

// Shared register modal shell.
//
// RESPONSIVE CONTRACT: `width` is a MAXIMUM, not a fixed size. Every register modal used to be a
// fixed pixel width, so a 460px dialog simply overflowed a 360px phone and its buttons became
// unreachable. Here the card is `min(width, 100vw - 24px)` and the body scrolls inside a bounded
// height, so the header and the primary action are always on screen.
//
// On phones the card sits at the BOTTOM of the viewport rather than the middle: that is where the
// thumb is, and it matches the cart sheet, so dialogs and the cart share one physical language.
const s = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "var(--scrim)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: 12,
  },
  head: {
    padding: "14px 16px",
    fontWeight: 800,
    fontSize: 16,
    borderBottom: "1px solid var(--line)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    color: "var(--ink)",
    flex: "0 0 auto",
  },
  x: {
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: 18,
    color: "var(--ink-2)",
    minWidth: 44,
    minHeight: 44,
    borderRadius: 10,
  },
  body: { padding: 16, overflow: "auto", WebkitOverflowScrolling: "touch" },
};

export default function Modal({ title, onClose, children, width = 420, zIndex }) {
  // Escape closes — a keyboard user must never be trapped in a dialog.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      style={zIndex ? { ...s.overlay, zIndex } : s.overlay}
      onClick={onClose}
      role="presentation"
      className="qv-modal-overlay"
    >
      <div
        className="qv-modal-card"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        style={{ maxWidth: width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={s.head}>
          <span>{title}</span>
          <button style={s.x} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={s.body}>{children}</div>
      </div>
    </div>
  );
}

export const modalStyles = {
  label: { fontSize: 13, color: "var(--ink-2)", marginBottom: 6, fontWeight: 500 },
  input: { width: "100%", height: 48, borderRadius: 10, border: "1px solid var(--line)", padding: "0 14px", fontSize: 16, outline: "none", boxSizing: "border-box", background: "var(--panel-2)", color: "var(--ink)" },
  toggleRow: { display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" },
  toggle: (active) => ({ flex: "1 1 120px", minHeight: 46, borderRadius: 10, border: active ? "2px solid var(--accent)" : "1px solid var(--line)", background: active ? "var(--accent-soft)" : "var(--panel-2)", color: "var(--ink)", fontWeight: 500, cursor: "pointer" }),
  primary: { width: "100%", minHeight: 52, marginTop: 16, borderRadius: 12, border: "none", background: "var(--accent)", color: "var(--accent-ink)", fontWeight: 800, fontSize: 16, cursor: "pointer" },
  ghost: { width: "100%", minHeight: 48, marginTop: 8, borderRadius: 10, border: "1px solid var(--line)", background: "var(--panel-2)", color: "var(--ink-2)", fontWeight: 500, cursor: "pointer" },
};
